import React from 'react'
import { RmnMatchStatus } from '../../types/rmn'

// Shared pieces for the client-facing Discovered views (Catalog > Products
// / Brands > Discovered) — status filters, pills, and the grouped L1/L2
// category options the import modal's required picker uses.

export const STATUS_FILTERS: Array<{ value: RmnMatchStatus; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'auto_matched', label: 'Auto-matched' },
  { value: 'promoted', label: 'In catalog' },
  { value: 'ignored', label: 'Ignored' },
]

export const isActionable = (status: RmnMatchStatus) =>
  status === 'pending' || status === 'auto_matched'

// Same palette as the internal DiscoveredReviewPanel, rendered in the
// TDC-1499 pill style.
const STATUS_STYLE: Record<RmnMatchStatus, { bg: string; fg: string; label: string }> = {
  pending: { bg: 'var(--surface-2)', fg: 'var(--text-2)', label: 'Pending' },
  auto_matched: { bg: 'rgba(36,116,221,.14)', fg: '#2474DD', label: 'Auto-matched' },
  promoted: { bg: 'var(--up-bg)', fg: 'var(--up)', label: 'In catalog' },
  ignored: { bg: 'var(--surface-2)', fg: 'var(--muted)', label: 'Ignored' },
}

export function StatusPill({ status }: { status: RmnMatchStatus }) {
  const style = STATUS_STYLE[status] || STATUS_STYLE.pending
  return (
    <span
      className="text-[11px] font-bold uppercase tracking-[0.5px] rounded-full px-2.5 py-1 whitespace-nowrap"
      style={{ background: style.bg, color: style.fg }}
    >
      {style.label}
    </span>
  )
}

// The matched canonical brand is already a network member — importing the
// row is a pure (re)link.
export function InNetworkPill() {
  return (
    <span
      className="text-[11px] font-bold uppercase tracking-[0.5px] rounded-full px-2.5 py-1 whitespace-nowrap"
      style={{ background: 'var(--up-bg)', color: 'var(--up)' }}
    >
      In network
    </span>
  )
}

// Dupe-safety callout for rows already matched to a canonical record:
// importing links, it never creates a second copy.
export function LinkedMatchNote({ name }: { name: string }) {
  return (
    <span className="text-[12px] text-text-2">
      Links to <span className="font-semibold text-text-1">{name}</span>
      <span className="text-muted"> — won&apos;t duplicate</span>
    </span>
  )
}

export interface CategoryOption {
  id: number
  name: string
  parent_id?: number | null
}

// Roots are selectable group headers; children indented beneath so either
// level can be picked (same shape the proposal wizard's dropdown uses).
export const groupedCategoryOptions = (categories: CategoryOption[]) =>
  categories
    .filter((c) => c.parent_id == null)
    .map((root) => {
      const children = categories.filter((c) => c.parent_id === root.id)
      return (
        <React.Fragment key={root.id}>
          <option value={root.id}>{root.name}</option>
          {children.map((child) => (
            <option key={child.id} value={child.id}>
              {`   ${root.name} › ${child.name}`}
            </option>
          ))}
        </React.Fragment>
      )
    })
